import React,{createContext,useContext,useState,useEffect,useCallback} from 'react';
    import * as brandKitsApi from '../lib/brandKitsApi';

    const BrandKitContext=createContext();

    export const useBrandKit=()=> {
      const context=useContext(BrandKitContext);
      if (!context) {
        throw new Error('useBrandKit must be used within a BrandKitProvider');
      }
      return context;
    };

    const DEFAULT_BRAND_KIT={
      id: 'default-kit-0',
      numericId: 0,
      name: 'Default Brand Kit',
      description: 'Professional brand',
      created_at: '2025-11-11T10:24:23.000Z',
      updated_at: '2025-11-11T10:24:23.000Z',
      user_id: 'system_default',
      colors: {
        primary: '#03245E',
        accent: '#E2C164',
        background: '#F3F7FA',
        secondary: '#D7B740',
        highlight: '#F7EFE2',
        text: '#111827',
      },
      fonts: {
        title: 'Anton',
        body: 'Helvetica World',
        subtitle: 'Montserrat',
        heading: 'League Spartan',
        highlight: 'Gistesy',
        primary: 'Helvetica World',
        secondary: 'Montserrat',
      },
      logos: [],
      watermarks: [],
    };

    export const BrandKitProvider=({children})=> {
      const [brandKits,setBrandKits]=useState([]);
      const [selectedKit,setSelectedKit]=useState(null);
      const [loading,setLoading]=useState(true);

      const loadBrandKits=useCallback(async ()=> {
        setLoading(true);
        try {
          let kits=await brandKitsApi.list();
          if (kits.length===0) {
            kits=[DEFAULT_BRAND_KIT];
          }
          setBrandKits(kits);
          const selectedId=localStorage.getItem('selectedBrandKitId');
          const kitFromStorage=kits.find(k=> k.id.toString()===selectedId);
          if (kitFromStorage) {
            setSelectedKit(kitFromStorage);
          } else if (kits.length > 0) {
            setSelectedKit(kits[0]);
            localStorage.setItem('selectedBrandKitId',kits[0].id.toString());
          } else {
            setSelectedKit(null);
            localStorage.removeItem('selectedBrandKitId');
          }
        } catch (error) {
          console.error('Failed to load brand kits:',error);
          setBrandKits([DEFAULT_BRAND_KIT]);
          setSelectedKit(DEFAULT_BRAND_KIT);
          localStorage.setItem('selectedBrandKitId',DEFAULT_BRAND_KIT.id.toString());
        } finally {
          setLoading(false);
        }
      },[]);

      useEffect(()=> {
        loadBrandKits();
      },[loadBrandKits]);

      const createBrandKit=async (kitData)=> {
        const newKit=await brandKitsApi.create(kitData);
        await loadBrandKits();
        return newKit;
      };

      const updateBrandKit=async (id,updates)=> {
        if (id===DEFAULT_BRAND_KIT.id) {
          console.warn("Updating the default kit is not persisted.");
          const updatedKit={
            ...brandKits.find(k=> k.id===id),
            ...updates,
            updated_at: new Date().toISOString()
          };
          setBrandKits(prev=> prev.map(k=> k.id===id ? updatedKit : k));
          if (selectedKit?.id===id) setSelectedKit(updatedKit);
          return updatedKit;
        }
        const updatedKit=await brandKitsApi.update(id,updates);
        await loadBrandKits();
        return updatedKit;
      };

      const deleteBrandKit=async (id)=> {
        if (id===DEFAULT_BRAND_KIT.id) {
          console.warn("Cannot delete the default kit.");
          return;
        }
        const response=await brandKitsApi.remove(id);
        await loadBrandKits();
        return response;
      };

      const selectBrandKit=(kit)=> {
        setSelectedKit(kit);
        localStorage.setItem('selectedBrandKitId',kit.id.toString());
      };

      const getBrandKit=(id)=> {
        return brandKits.find(kit=> kit.id.toString()===id.toString());
      };

      const value={
        brandKits,
        selectedKit,
        loading,
        createBrandKit,
        updateBrandKit,
        deleteBrandKit,
        selectBrandKit,
        getBrandKit,
        refreshBrandKits: loadBrandKits,
      };

      return (
        <BrandKitContext.Provider value={value}>
          {children}
        </BrandKitContext.Provider>
      );
    };